// The report half of the Snapchat Warrant Processor: everything shown once a return has been
// parsed. Kept apart from the upload page so the Case Analyzer desktop app can show a return
// it has already read with exactly the same views (it passes its own top-bar brand, a Close
// button and its own media map instead of the "Load media" picker).

import React, { useState, useMemo } from 'react';
import { ReviewCaveat, GeneratedMark, GeneratedLegend } from '../ReviewAid';
import SocialAnalysis, { socialNav, isAnalysisPage } from '../analysis/SocialAnalysis';
import { snapchatInput } from '../../utils/socialAnalysis';

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  bg:      '#0f172a',
  surface: '#1e293b',
  surf2:   '#263044',
  border:  '#334155',
  text:    '#e2e8f0',
  muted:   '#94a3b8',
  yellow:  '#fbbf24',
  cyan:    '#67e8f9',
};

// ─── Media helpers ────────────────────────────────────────────────────────────
export function buildMediaMap(files) {
  const map = {};
  for (const f of Array.from(files)) {
    const url = URL.createObjectURL(f);
    map[f.name] = url;
    const noExt = f.name.replace(/\.[^.]+$/, '');
    map[noExt] = url;
    if (f.webkitRelativePath) {
      map[f.webkitRelativePath] = url;
      const parts = f.webkitRelativePath.split('/');
      if (parts.length >= 2) map[parts.slice(-2).join('/')] = url;
    }
  }
  return map;
}
function resolveMedia(key, mediaMap) {
  if (!key || !mediaMap) return null;
  return mediaMap[key] || mediaMap[key.replace(/\.[^.]+$/, '')] || mediaMap[key.split('/').pop()] || null;
}
function findProfilePhoto(mediaMap) {
  for (const key of Object.keys(mediaMap)) {
    if (key.toLowerCase().includes('public_profile') && /\.(jpg|jpeg|png|gif|webp)$/i.test(key)) return mediaMap[key];
  }
  return null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function NavBtn({ label, badge, active, onClick }) {
  return (
    <div style={{ padding: '2px 8px' }}>
      <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', border: 'none', borderRadius: 6, background: active ? C.yellow : 'transparent', color: active ? '#1c1400' : C.muted, textAlign: 'left', cursor: 'pointer', fontSize: 13, transition: 'background .15s, color .15s', fontWeight: active ? 700 : 400 }}>
        {label}
        {badge !== undefined && badge > 0 && (
          <span style={{ marginLeft: 'auto', fontSize: 11, background: 'rgba(255,255,255,.15)', padding: '1px 6px', borderRadius: 10 }}>{badge}</span>
        )}
      </button>
    </div>
  );
}

function StatCard({ number, label, color }) {
  return (
    <div style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: color || C.yellow }}>{number}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono, first }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13, borderTop: first ? 'none' : `1px solid ${C.border}` }}>
      <span style={{ color: C.muted, width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ wordBreak: 'break-word', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined }}>{value}</span>
    </div>
  );
}

function Th({ children, sortKey, sort, onSort }) {
  const active = sort?.key === sortKey;
  return (
    <th onClick={sortKey ? () => onSort(sortKey) : undefined} style={{ textAlign: 'left', padding: '8px 12px', color: active ? C.yellow : C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap', cursor: sortKey ? 'pointer' : 'default', userSelect: 'none' }}>
      {children}{sortKey ? (active ? (sort.asc ? ' ↑' : ' ↓') : ' ↕') : ''}
    </th>
  );
}

function GenericTables({ tables }) {
  if (!tables || tables.every(t => !t.rows.length)) return <p style={{ color: C.muted, fontSize: 13 }}>No data available.</p>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {tables.map((t, ti) => t.rows.length > 0 && (
        <div key={ti}>
          {t.label && <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow, marginBottom: 10 }}>{t.label}</div>}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr>{t.headers.map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
              <tbody>
                {t.rows.map((row, ri) => (
                  <tr key={ri}>{t.headers.map((h, ci) => <td key={ci} style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, wordBreak: 'break-word', maxWidth: 320, fontSize: 12 }}>{row[ci] || ''}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Print conversation ───────────────────────────────────────────────────────
function printConversation(convo, targetUserId, username) {
  const win = window.open('', '_blank', 'width=820,height=960');
  const rows = convo.messages.map(msg => {
    const isMine = (targetUserId && msg.sender_id === targetUserId) ||
                   (username && msg.sender === username);
    const isErased = msg.content_type?.startsWith('StatusErased');
    const isMedia = !msg.text && !isErased && msg.content_type && msg.content_type !== 'Text';
    const text = isErased ? '[deleted message]' : isMedia ? `[${msg.content_type}]` : (msg.text || '[no text]');
    const bubbleBg = isMine ? '#f59e0b' : '#e2e8f0';
    const bubbleColor = isMine ? '#1c1400' : '#0f172a';
    const extras = [
      msg.is_saved === 'TRUE' ? '<span style="color:#16a34a;">saved</span>' : '',
      msg.screenshotted_by ? '<span style="color:#dc2626;">screenshotted</span>' : '',
      msg.upload_ip ? `<span style="font-family:monospace;color:#0369a1;">${msg.upload_ip}</span>` : '',
    ].filter(Boolean).join(' &nbsp;');
    return `<div style="display:flex;flex-direction:column;max-width:70%;align-self:${isMine ? 'flex-end' : 'flex-start'};align-items:${isMine ? 'flex-end' : 'flex-start'};margin-bottom:10px;">
      ${!isMine ? `<div style="font-size:11px;color:#64748b;margin-bottom:3px;font-weight:600;">${msg.sender || ''}</div>` : ''}
      <div style="background:${bubbleBg};color:${bubbleColor};padding:8px 12px;border-radius:12px;${isMine ? 'border-bottom-right-radius:3px;' : 'border-bottom-left-radius:3px;'}font-size:13px;line-height:1.55;word-break:break-word;${isErased || isMedia ? 'font-style:italic;opacity:0.6;' : ''}">${text}</div>
      <div style="font-size:10px;color:#94a3b8;margin-top:3px;display:flex;gap:8px;">${msg.timestamp?.slice(0, 16) || ''}${extras ? ` &nbsp; ${extras}` : ''}</div>
    </div>`;
  }).join('');

  win.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <title>Snapchat – ${convo.title}</title>
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:24px;color:#0f172a;background:#fff;}
      .hdr{border-bottom:2px solid #e2e8f0;padding-bottom:14px;margin-bottom:20px;}
      .hdr h2{margin:0 0 6px;font-size:19px;}
      .hdr p{margin:2px 0;font-size:12px;color:#64748b;}
      .msgs{display:flex;flex-direction:column;width:100%;}
      @media print{body{padding:10px;}.no-print{display:none;}}
    </style>
  </head><body>
    <div class="hdr">
      <h2>${convo.title}</h2>
      <p>Participants: ${convo.participants.join(', ')}</p>
      <p>${convo.messages.length} messages &nbsp;·&nbsp; ${convo.messages[0]?.timestamp?.slice(0, 10) || ''} – ${convo.lastTimestamp?.slice(0, 10) || ''}</p>
    </div>
    <div class="msgs">${rows}</div>
    <script>window.onload=()=>{window.print();}<\/script>
  </body></html>`);
  win.document.close();
  win.focus();
}

export default function SnapchatReport({ data, mediaMap = {}, onLoadMedia = null, onReset, resetLabel = '← New File', brand = null }) {
  const analysisInput = useMemo(() => (data ? snapchatInput(data) : null), [data]);
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarFilter, setSidebarFilter] = useState('');
  const [ipFilter, setIpFilter]           = useState('');
  const [ipSort, setIpSort]               = useState(null);
  const [friendFilter, setFriendFilter]   = useState('');
  const [friendType, setFriendType]       = useState('ALL');
  const [selectedConvo, setSelectedConvo] = useState(0);
  const [chatSearch, setChatSearch]       = useState('');
  const [globalChatSearch, setGlobalChatSearch] = useState('');
  const [memFilter, setMemFilter]         = useState('');

  // ── Report ─────────────────────────────────────────────────────────────────
  const { targetInfo, dateRange, subscriber, ipRecords, topIps, conversations, friends, friendsList, memories, stories, publicProfile, aiConvos, groupStory, publicStory, locPriv, emailEvents, reportedConvos, reportedMedia, sharedStory, detectedUrl } = data;
  const { main, extended, privacy, usernameHistory, accountChanges, homeWork } = subscriber;
  const hasMediaLoaded = Object.keys(mediaMap).length > 0;
  const profilePhotoUrl = findProfilePhoto(mediaMap);

  const sidebarSections = [
    { key: 'overview',          label: 'Overview' },
    { key: 'account-info',      label: 'Account Info',            count: accountChanges.length || undefined },
    { key: 'conversations',     label: 'Conversations',           count: conversations.length },
    ...(aiConvos.some(t => t.rows.length) ? [{ key: 'ai-conversations', label: 'AI Conversations (My AI)', count: aiConvos.reduce((s, t) => s + t.rows.length, 0) }] : []),
    { key: 'ip-data',           label: 'IP Data',                 count: ipRecords.length },
    { key: 'friends',           label: 'Friends / Blocks',        count: friends.length },
    ...(friendsList.length ? [{ key: 'friends-list', label: 'Friends List', count: friendsList.length }] : []),
    ...(memories.length ? [{ key: 'memories', label: 'Memories', count: memories.length }] : []),
    ...(stories.length ? [{ key: 'story', label: 'Story', count: stories.length }] : []),
    ...(publicProfile ? [{ key: 'public-profile', label: 'Public Profile' }] : []),
    ...(publicStory.some(t => t.rows.length) ? [{ key: 'public-story', label: 'Public Story', count: publicStory.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(groupStory.some(t => t.rows.length) ? [{ key: 'group-story', label: 'Group Story', count: groupStory.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(sharedStory.some(t => t.rows.length) ? [{ key: 'shared-story', label: 'Shared Story', count: sharedStory.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(locPriv.some(t => t.rows.length) ? [{ key: 'location-privacy', label: 'Location Privacy', count: locPriv.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(emailEvents.some(t => t.rows.length) ? [{ key: 'email-events', label: 'Email Events', count: emailEvents.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(reportedConvos.some(t => t.rows.length) ? [{ key: 'reported-convos', label: 'Reported Conversations', count: reportedConvos.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(reportedMedia.some(t => t.rows.length) ? [{ key: 'reported-media', label: 'Reported Media', count: reportedMedia.reduce((s, t) => s + t.rows.length, 0) }] : []),
    ...(detectedUrl.some(t => t.rows.length) ? [{ key: 'detected-url', label: 'Detected URLs', count: detectedUrl.reduce((s, t) => s + t.rows.length, 0) }] : []),
  ];

  const filteredSidebar = sidebarFilter ? sidebarSections.filter(s => s.label.toLowerCase().includes(sidebarFilter.toLowerCase())) : sidebarSections;

  // Conversation helpers
  const visibleConvos = globalChatSearch
    ? conversations.filter(c => c.messages.some(m => m.text.toLowerCase().includes(globalChatSearch.toLowerCase()) || m.sender.toLowerCase().includes(globalChatSearch.toLowerCase())))
    : conversations;
  const currentConvo = conversations[selectedConvo];
  const chatMsgs = currentConvo
    ? (chatSearch ? currentConvo.messages.filter(m => m.text.toLowerCase().includes(chatSearch.toLowerCase()) || m.sender.toLowerCase().includes(chatSearch.toLowerCase())) : currentConvo.messages)
    : [];

  // IP helpers
  const filteredIps = ipFilter ? ipRecords.filter(r => `${r.ip} ${r.user_agent} ${r.source} ${r.device_id}`.toLowerCase().includes(ipFilter.toLowerCase())) : ipRecords;
  const sortedIps = ipSort ? [...filteredIps].sort((a, b) => { const va = a[ipSort.key] || ''; const vb = b[ipSort.key] || ''; return ipSort.asc ? va.localeCompare(vb) : vb.localeCompare(va); }) : filteredIps;

  // Friends helpers
  const filteredFriends = friends.filter(f => {
    if (friendType !== 'ALL' && f.type !== friendType) return false;
    if (friendFilter && !`${f.adder} ${f.added} ${f.friending_method}`.toLowerCase().includes(friendFilter.toLowerCase())) return false;
    return true;
  });

  const filteredMems = memFilter ? memories.filter(m => `${m.timestamp} ${m.latitude} ${m.longitude}`.toLowerCase().includes(memFilter.toLowerCase())) : memories;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg, color: C.text, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* Navbar */}
      <div style={{ height: 58, background: '#0a0f1e', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 12, flexShrink: 0 }}>
        {brand || (
          <>
            <a href="/" style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', textDecoration: 'none' }}>LE Tech Tools</a>
            <span style={{ color: C.border, fontSize: 18, fontWeight: 300, margin: '0 4px' }}>/</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.yellow }}>Snapchat Warrant Processor</span>
          </>
        )}
        {main.username && (<><span style={{ color: C.border, fontSize: 18, fontWeight: 300, margin: '0 4px' }}>/</span><span style={{ fontSize: 13, color: C.muted }}>@{main.username}</span></>)}
        {!hasMediaLoaded && onLoadMedia
          ? <button onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.multiple = true; i.webkitdirectory = true; i.onchange = e => onLoadMedia(e.target.files); i.click(); }} style={{ marginLeft: 'auto', fontSize: 12, color: C.muted, background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>+ Load media</button>
          : <span style={{ marginLeft: 'auto', fontSize: 12, color: hasMediaLoaded ? '#86efac' : C.muted }}>{hasMediaLoaded ? '✓ Media loaded' : 'No media files'}</span>
        }
        <button onClick={onReset} style={{ fontSize: 13, color: '#64748b', background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }}>{resetLabel}</button>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <aside style={{ width: 230, background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
            <input type="text" placeholder="Filter sections…" value={sidebarFilter} onChange={e => setSidebarFilter(e.target.value)} style={{ width: '100%', background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
            {filteredSidebar.map(s => <NavBtn key={s.key} label={s.label} badge={s.count} active={activeSection === s.key} onClick={() => setActiveSection(s.key)} />)}
            {analysisInput && (
              <>
                <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
                <div style={{ padding: '4px 18px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.muted }}>
                  Analysis <span title="Generated by LE Tech Tools. Not part of the original legal return." style={{ color: '#fbbf24' }}>✱</span>
                </div>
                {socialNav(analysisInput).filter(s => !sidebarFilter || s.label.toLowerCase().includes(sidebarFilter.toLowerCase())).map(s => (
                  <NavBtn key={s.key} label={s.label} badge={s.count} active={activeSection === s.key} onClick={() => setActiveSection(s.key)} />
                ))}
              </>
            )}
          </div>
        </aside>

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {isAnalysisPage(activeSection) && analysisInput && <SocialAnalysis page={activeSection} input={analysisInput} />}

          {/* ── Overview ── */}
          {activeSection === 'overview' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Overview</div>
              <ReviewCaveat dark what="the actual returns from Snap (Snapchat)" />
              {topIps.length > 0 && <GeneratedLegend dark>Generated by LE Tech Tools (the Top 10 IP ranking). Not part of the original legal return. Everything else on this page comes from the return.</GeneratedLegend>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                <StatCard number={conversations.length} label="Conversations" />
                <StatCard number={ipRecords.length} label="IP Records" />
                <StatCard number={friends.filter(f => f.type === 'FRIENDS').length} label="Friends" color="#86efac" />
                <StatCard number={friends.filter(f => f.type === 'BLOCKED').length} label="Blocked" color="#f87171" />
                {memories.length > 0 && <StatCard number={memories.length} label="Memories" color={C.cyan} />}
                {extended.follower_count && <StatCard number={extended.follower_count} label="Subscribers" />}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <InfoCard title="Account Information">
                  {profilePhotoUrl && <img src={profilePhotoUrl} alt="Profile" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', marginBottom: 12, display: 'block' }} />}
                  {[
                    { label: 'Username',     value: main.username },
                    { label: 'Display Name', value: main.display_name },
                    { label: 'User ID',      value: main.user_id,     mono: true },
                    { label: 'Status',       value: main.status },
                    { label: 'Email',        value: main.email },
                    { label: 'Phone',        value: main.phone,       mono: true },
                    { label: 'Created',      value: main.created },
                    { label: 'Creation IP',  value: main.creation_ip, mono: true },
                    { label: 'Last Active',  value: extended.last_active },
                    { label: 'Birthdate',    value: extended.birthdate },
                    { label: 'App Version',  value: extended.app_version },
                    { label: '2FA Status',   value: extended.two_fa },
                    { label: 'Snap Privacy', value: privacy.snap },
                    { label: 'Story Privacy', value: privacy.story },
                    { label: 'Date Range',   value: dateRange },
                    { label: 'Target Phone', value: targetInfo.phone, mono: true },
                  ].filter(r => r.value).map((r, i) => <InfoRow key={r.label} label={r.label} value={r.value} mono={r.mono} first={i === 0} />)}
                </InfoCard>
                {topIps.length > 0 && (
                  <InfoCard title={<>Top 10 IP Addresses<GeneratedMark dark /></>}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead><tr>
                        <th style={{ textAlign: 'left', padding: '7px 0', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, borderBottom: `2px solid ${C.border}` }}>IP</th>
                        <th style={{ textAlign: 'right', padding: '7px 0', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, borderBottom: `2px solid ${C.border}`, width: 50 }}>Hits</th>
                      </tr></thead>
                      <tbody>
                        {topIps.map(({ ip, count }) => (
                          <tr key={ip}>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ height: 6, background: C.yellow, borderRadius: 3, width: Math.max(4, Math.round(count / topIps[0].count * 70)), flexShrink: 0 }} />
                                {ip}
                              </div>
                            </td>
                            <td style={{ padding: '6px 0', borderBottom: `1px solid ${C.border}`, color: C.muted, textAlign: 'right' }}>{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </InfoCard>
                )}
              </div>
              {homeWork.length > 0 && (
                <InfoCard title="Declared Home / Work">
                  {homeWork.map((loc, i) => <InfoRow key={i} label={loc.place} value={`${loc.latitude}, ${loc.longitude}${loc.isHidden === 'true' ? ' (hidden)' : ''}`} mono first={i === 0} />)}
                </InfoCard>
              )}
            </div>
          )}

          {/* ── Account Info ── */}
          {activeSection === 'account-info' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Account Info</div>
              {usernameHistory.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow, marginBottom: 10 }}>Username History</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr>{['Old Username','New Username','Timestamp'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {usernameHistory.map((r, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace' }}>{r.old}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', fontWeight: 600 }}>{r.new}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{r.timestamp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {accountChanges.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.yellow, marginBottom: 10 }}>Account Change History ({accountChanges.length})</div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead><tr>{['Date','Action','Old Value','New Value','Reason'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {accountChanges.map((r, i) => (
                          <tr key={i}>
                            <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', color: C.muted }}>{r.date.slice(0, 10)}</td>
                            <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.yellow, fontFamily: 'monospace', fontSize: 12 }}>{r.action}</td>
                            <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, maxWidth: 200, wordBreak: 'break-word' }}>{r.old_value}</td>
                            <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, maxWidth: 200, wordBreak: 'break-word', fontWeight: 600 }}>{r.new_value}</td>
                            <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted, fontSize: 12 }}>{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Conversations ── */}
          {activeSection === 'conversations' && (
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* List */}
              <div style={{ width: 260, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0, background: C.surf2 }}>
                <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
                  <input type="text" placeholder="Search all conversations…" value={globalChatSearch} onChange={e => setGlobalChatSearch(e.target.value)} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {visibleConvos.map(c => {
                    const realIdx = conversations.indexOf(c);
                    const active = realIdx === selectedConvo;
                    const last = c.messages[c.messages.length - 1];
                    return (
                      <div key={c.cid} onClick={() => { setSelectedConvo(realIdx); setChatSearch(''); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: active ? C.surface : 'transparent', borderLeft: active ? `3px solid ${C.yellow}` : '3px solid transparent' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: active ? C.yellow : C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{last?.text || `[${last?.content_type || 'media'}]`}</div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{c.lastTimestamp.slice(0, 10)} · {c.messages.length} msgs</div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: '8px 12px', borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>{visibleConvos.length} conversation{visibleConvos.length !== 1 ? 's' : ''}</div>
              </div>
              {/* Messages */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {currentConvo ? (
                  <>
                    <div style={{ padding: '10px 18px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentConvo.title}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{currentConvo.participants.join(', ')} · {currentConvo.messages.length} messages</div>
                      </div>
                      <button onClick={() => printConversation(currentConvo, targetInfo.userId || main.user_id, main.username)} style={{ flexShrink: 0, fontSize: 12, padding: '5px 12px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Print / PDF
                      </button>
                    </div>
                    <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                      <input type="text" placeholder="Search messages…" value={chatSearch} onChange={e => setChatSearch(e.target.value)} style={{ width: '100%', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {chatMsgs.length === 0
                        ? <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: 40 }}>{chatSearch ? 'No messages match.' : 'No messages.'}</div>
                        : chatMsgs.map((msg, i) => {
                          const targetUserId = targetInfo.userId || main.user_id;
                          const isMine = (targetUserId && msg.sender_id === targetUserId) ||
                                         (main.username && msg.sender === main.username);
                          const isErased = msg.content_type?.startsWith('StatusErased');
                          const isMedia = !msg.text && !isErased && msg.content_type && msg.content_type !== 'Text';
                          const mediaSrc = isMedia && msg.media_id ? resolveMedia(msg.media_id, mediaMap) : null;
                          const isVideoMedia = mediaSrc && (msg.content_type?.toLowerCase().includes('video') || /\.(mp4|mov|webm)$/i.test(msg.media_id || ''));
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', maxWidth: '72%', alignSelf: isMine ? 'flex-end' : 'flex-start', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
                              {!isMine && <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{msg.sender}</div>}
                              <div style={{ borderRadius: 12, borderBottomRightRadius: isMine ? 3 : 12, borderBottomLeftRadius: isMine ? 12 : 3, overflow: 'hidden', background: isMine ? C.yellow : C.surface, color: isMine ? '#1c1400' : C.text, ...(mediaSrc ? {} : { padding: '8px 12px', fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }), fontStyle: (isErased || (isMedia && !mediaSrc)) ? 'italic' : 'normal', opacity: isErased ? 0.55 : 1 }}>
                                {mediaSrc
                                  ? (isVideoMedia
                                      ? <video src={mediaSrc} controls style={{ display: 'block', maxWidth: 260, maxHeight: 320 }} />
                                      : <img src={mediaSrc} alt={msg.content_type || 'media'} style={{ display: 'block', maxWidth: 260, maxHeight: 320, objectFit: 'contain' }} />)
                                  : (isErased ? '[deleted message]' : isMedia ? `[${msg.content_type}]` : (msg.text || '[no text]'))}
                              </div>
                              <div style={{ fontSize: 10, color: C.muted, marginTop: 2, display: 'flex', gap: 8 }}>
                                <span>{msg.timestamp.slice(0, 16)}</span>
                                {msg.is_saved === 'TRUE' && <span style={{ color: '#86efac' }}>saved</span>}
                                {msg.screenshotted_by && <span style={{ color: '#fca5a5' }}>screenshotted</span>}
                                {msg.upload_ip && <span style={{ fontFamily: 'monospace', color: C.cyan }}>{msg.upload_ip}</span>}
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </>
                ) : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted }}>Select a conversation</div>}
              </div>
            </div>
          )}

          {/* ── AI Conversations ── */}
          {activeSection === 'ai-conversations' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>AI Conversations (My AI)</div>
              <GenericTables tables={aiConvos} />
            </div>
          )}

          {/* ── IP Data ── */}
          {activeSection === 'ip-data' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>IP Data — {ipRecords.length.toLocaleString()} records</div>
              <input type="text" placeholder="Filter by IP, source, or device…" value={ipFilter} onChange={e => setIpFilter(e.target.value)} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {[['IP Address','ip'],['Timestamp','timestamp'],['Source','source'],['User Agent',null],['Device ID',null],['Port',null]].map(([h, k]) => (
                        <Th key={h} sortKey={k} sort={ipSort} onSort={key => setIpSort(prev => ({ key, asc: prev?.key === key ? !prev.asc : true }))}>{h}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedIps.map((r, i) => (
                      <tr key={i}>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan, whiteSpace: 'nowrap' }}>{r.ip}</td>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', color: C.muted }}>{r.timestamp}</td>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, maxWidth: 180, wordBreak: 'break-word' }}>{r.source}</td>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, maxWidth: 220, wordBreak: 'break-word', color: C.muted }}>{r.user_agent}</td>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 11, color: C.muted }}>{r.device_id}</td>
                        <td style={{ padding: '6px 12px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{r.port}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>{sortedIps.length.toLocaleString()} records{ipFilter ? ' (filtered)' : ''}</div>
            </div>
          )}

          {/* ── Friends / Blocks ── */}
          {activeSection === 'friends' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Friends / Blocks — {friends.length}</div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input type="text" placeholder="Filter by username or method…" value={friendFilter} onChange={e => setFriendFilter(e.target.value)} style={{ flex: 1, minWidth: 200, background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none' }} />
                {['ALL', 'FRIENDS', 'BLOCKED'].map(t => (
                  <button key={t} onClick={() => setFriendType(t)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${friendType === t ? C.yellow : C.border}`, background: friendType === t ? C.yellow : 'transparent', color: friendType === t ? '#1c1400' : C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{t === 'ALL' ? 'All' : t === 'FRIENDS' ? 'Friends' : 'Blocked'}</button>
                ))}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>{['Adder','Added','Type','Created','How Added'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {filteredFriends.map((f, i) => (
                      <tr key={i}>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace' }}>{f.adder}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace' }}>{f.added}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: f.type === 'BLOCKED' ? 'rgba(248,113,113,0.15)' : 'rgba(134,239,172,0.15)', color: f.type === 'BLOCKED' ? '#fca5a5' : '#86efac', fontWeight: 600 }}>{f.type}</span>
                        </td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted, whiteSpace: 'nowrap' }}>{f.created.slice(0, 10)}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted, fontSize: 12 }}>{f.friending_method.replace(/_/g, ' ').toLowerCase()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>{filteredFriends.length} records</div>
            </div>
          )}

          {/* ── Friends List ── */}
          {activeSection === 'friends-list' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10, marginBottom: 16 }}>Friends List — {friendsList.length} current friends</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                {friendsList.map((u, i) => <div key={i} style={{ padding: '8px 12px', background: C.surf2, borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 13 }}>{u}</div>)}
              </div>
            </div>
          )}

          {/* ── Memories ── */}
          {activeSection === 'memories' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                Memories — {memories.length}
                {!hasMediaLoaded && <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 12 }}>(load media folder above to display images)</span>}
              </div>
              <input type="text" placeholder="Filter by date or coordinates…" value={memFilter} onChange={e => setMemFilter(e.target.value)} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 12px', fontSize: 13, color: C.text, outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              {hasMediaLoaded ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                  {filteredMems.map((m, i) => {
                    const imgSrc = resolveMedia(m.media_id, mediaMap);
                    return (
                      <div key={i} style={{ background: C.surf2, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
                        {imgSrc
                          ? <img src={imgSrc} alt={m.media_id} style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block' }} />
                          : <div style={{ height: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: C.bg, color: C.muted, gap: 4 }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                              <span style={{ fontSize: 9, maxWidth: 180, textAlign: 'center', wordBreak: 'break-all' }}>{m.media_id}</span>
                            </div>
                        }
                        <div style={{ padding: '10px 12px', fontSize: 12 }}>
                          <div style={{ color: C.muted, marginBottom: 2 }}>{m.timestamp.slice(0, 16)}</div>
                          {m.latitude && <div style={{ color: C.cyan, fontFamily: 'monospace', fontSize: 11 }}>{m.latitude}, {m.longitude}</div>}
                          {m.duration && parseFloat(m.duration) > 0 && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{parseFloat(m.duration).toFixed(1)}s</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr>{['Timestamp','Latitude','Longitude','Duration (s)','Source','Encrypted'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {filteredMems.map((m, i) => (
                        <tr key={i}>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{m.timestamp}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>{m.latitude}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>{m.longitude}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}` }}>{parseFloat(m.duration || 0).toFixed(1)}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{m.source_type}</td>
                          <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{m.encrypted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ fontSize: 12, color: C.muted }}>{filteredMems.length} records</div>
            </div>
          )}

          {/* ── Story ── */}
          {activeSection === 'story' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Story — {stories.length}</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>{['Timestamp','Media Type','Upload IP','Source Port'].map(h => <th key={h} style={{ textAlign: 'left', padding: '7px 10px', color: C.muted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {stories.map((s, i) => (
                      <tr key={i}>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{s.timestamp}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{s.media_type || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, fontFamily: 'monospace', color: C.cyan }}>{s.upload_ip || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: `1px solid ${C.border}`, color: C.muted }}>{s.source_port || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Public Profile ── */}
          {activeSection === 'public-profile' && publicProfile && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>Public Profile</div>
              <InfoCard title="Details">
                {[
                  { label: 'Display Name',  value: publicProfile.display_name },
                  { label: 'Designation',   value: publicProfile.designation },
                  { label: 'Bio',           value: publicProfile.bio },
                  { label: 'Location',      value: publicProfile.stated_location },
                  { label: 'Subscribers',   value: publicProfile.subscriber_count },
                  { label: 'Website',       value: publicProfile.website },
                  { label: 'Profile Created', value: publicProfile.created_at },
                  { label: 'Is Frozen',     value: publicProfile.is_frozen },
                ].filter(r => r.value).map((r, i) => <InfoRow key={r.label} label={r.label} value={r.value} first={i === 0} />)}
              </InfoCard>
            </div>
          )}

          {/* ── Generic table sections ── */}
          {[
            { key: 'public-story',     label: 'Public Story',            tables: publicStory },
            { key: 'group-story',      label: 'Group Story',             tables: groupStory },
            { key: 'shared-story',     label: 'Shared Story',            tables: sharedStory },
            { key: 'location-privacy', label: 'Location Privacy',        tables: locPriv },
            { key: 'email-events',     label: 'Email Events',            tables: emailEvents },
            { key: 'reported-convos',  label: 'Reported Conversations',  tables: reportedConvos },
            { key: 'reported-media',   label: 'Reported Media Metadata', tables: reportedMedia },
            { key: 'detected-url',     label: 'Detected URLs',           tables: detectedUrl },
          ].map(({ key, label, tables }) => activeSection === key && (
            <div key={key} style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>{label}</div>
              <GenericTables tables={tables} />
            </div>
          ))}

        </main>
      </div>
    </div>
  );
}
